<script setup>
  import {computed, inject, onMounted, onUnmounted, ref, watch} from "vue";

  import DeveloperContainer from "@components/_DevOnly/DeveloperContainer.vue";
  import ComponentDoNotShowAgain from "@components/Checkboxes/DoNotShowAgain.vue";
  import ComponentCommentsDialog from "@components/Dialogs/CommentsDialog.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentCoreInfoDialog from "@components/Dialogs/CoreInfoDialog/CoreInfoDialog.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentIconAiSparkle from "@components/Icons/IconAiSparkle.vue";
  import {useForm, usePage} from "@inertiajs/vue3";
  import PagesRecordResultListClaimInteractionChat from "@pages/Records/Components/Result/ResultList/ClaimInteractionChat.vue";
  import PagesRecordResultListClaimInteractionDocumentation from "@pages/Records/Components/Result/ResultList/ClaimInteractionDocumentation.vue";
  import PagesRecordResultListClaimInteractionPdf from "@pages/Records/Components/Result/ResultList/ClaimInteractionPdf.vue";
  import PagesRecordResultListClaimTextBox from "@pages/Records/Components/Result/ResultList/ClaimTextBox.vue";
  import ComponentPatientTranslateDialog from "@pages/Records/Components/Result/ResultList/PatientTranslateDialog.vue";
  import {MedBadge, MedButton, MedCheckbox, MedIcon, MedTooltip} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {debounce} from "es-toolkit";

  defineOptions({
    name: "PagesRecordResultListClaimItem",
  });

  const props = defineProps({
    patientId: {
      type: String,
      required: true,
    },
    recordId: {
      type: String,
      required: true,
    },
    mcCheckId: {
      type: Number,
      required: true,
    },
    claim: {
      type: Object,
      required: true,
    },
    claimNumber: {
      type: Number,
      required: true,
    },
    hasSiblingClaims: {
      type: Boolean,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    visibleTabs: {
      type: Array,
      default: null,
    },
    aiPrioritized: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["open:shareDialog"]);

  const privacy = inject("$privacy");
  const pageProperties = usePage().props;
  const refCoreInfoDialog = ref(null);
  const refConfirmCommunicationDoctor = ref(null);
  const refConfirmCommunicationPatient = ref(null);
  const refCommentsDialog = ref(null);
  const refConfidentialCommentsDialog = ref(null);
  const refAiConfirmationDialog = ref(null);
  const refPatientTranslateDialog = ref(null);
  const open = ref(false);
  const isTabVisible = (tab) => !props.visibleTabs || props.visibleTabs.includes(tab);
  const activeTab = ref(isTabVisible("tab-doc") ? "tab-doc" : (props.visibleTabs?.[0] ?? "tab-doc"));

  const activeInteractionTab = ref("tab-pdf");
  const isFloating = ref(false);
  const visibleTabPanels = ref(true);
  const userHasSeenCommunicationPatient = ref(false); // micro-cache

  const comments = inject("comments");

  const claimComments = computed(() => {
    return comments.value.filter(
      (comment) => comment.section === props.mcCheckId + "-" + props.claim.section && comment.confidential === false,
    );
  });
  const claimConfidentialComments = computed(() => {
    return comments.value.filter(
      (comment) => comment.section === props.mcCheckId + "-" + props.claim.section && comment.confidential === true,
    );
  });
  // Only these checks carry the ABDATA rating, and only their summary ends with
  // the key measure appended behind the measure in brackets.
  const hasAbdataDetails = computed(
    () => [1, 31, 2, 24, 17, 30, 32, 33, 34].includes(props.mcCheckId) && !!props.claim?.causedby?.extra,
  );

  // The ABDATA colour is rendered between summary and key measure, so the two
  // have to be split apart. This must stay limited to the checks above: every
  // other summary would silently lose everything behind its first bracket.
  const summaryParts = computed(() => {
    const summary = props.claim?.summary ?? "";

    return hasAbdataDetails.value ? summary.split(/(?<!\d|BMP|eMP|eML)\) /) : [summary];
  });

  const claimSummary = computed(() => {
    const parts = summaryParts.value;
    if (parts[1] && parts[1].length > 0) {
      return parts[0] + ")";
    }
    return parts[0] ?? "";
  });
  const interactionKeyMeasure = computed(() => summaryParts.value[1] ?? "");

  const patientTranslateIsStatic = computed(() => {
    return props.claim?.check.is_static_translatable ?? false;
  });

  const patientTranslateIsHidden = computed(() => {
    return props.claim?.check.is_already_patient_translated ?? false;
  });

  const patientTranslateButtonDisabled = computed(() => {
    if (patientTranslateIsStatic.value) {
      return false;
    }

    return (
      !pageProperties.can.activate_ai_features ||
      !pageProperties.can.allow_ai_patient_translate ||
      !props.claim.check?.is_ai_translatable
    );
  });

  const patientTranslateButtonTooltip = computed(() => {
    if (patientTranslateIsStatic.value) {
      return null;
    }

    if (!pageProperties.can.allow_ai_patient_translate) {
      return "Diese KI-Funktion steht Ihnen im MediCheck Pro zur Verfügung.";
    } else if (!pageProperties.can.activate_ai_features) {
      return "Um die Funktion zu nutzen, schalten Sie bitte die KI-Funktionen in Ihren Einstellungen frei.";
    } else if (!props.claim.check.is_ai_translatable) {
      return "Diese Funktion steht Ihnen in Kürze zur Verfügung.";
    }

    return null;
  });

  const form = useForm({
    // problem and solution texts:
    problemdoc: props.claim.problemdoc,
    solutiondoc: props.claim.solutiondoc,
    problempatient: props.claim.problempatient,
    solutionpatient: props.claim.solutionpatient,

    // interactions:
    communicationdoc: props.claim.communicationdoc,
    communicationpatient: props.claim.communicationpatient,
    hidden: props.claim.hidden,
    usernote: props.claim.usernote,
    usernotepatient: props.claim.usernotepatient,
  });

  const save = debounce(function () {
    form.patch(
      route("claims.update", {
        patient: props.patientId,
        record: props.recordId,
        check: props.claim.check_id,
        claim: props.claim.id,
      }),
      {
        preserveScroll: true,
        preserveState: true,
        only: ["flash", "errors", "checks"],
      },
    );
  }, 800);

  watch(
    () => [
      form.problemdoc, // 0
      form.solutiondoc, // 1
      form.problempatient, // 2
      form.solutionpatient, // 3
      form.communicationdoc, // 4
      form.communicationpatient, // 5
      form.hidden, // 6
      form.usernote, // 7
      form.usernotepatient, // 8
    ],
    (newValues, oldValues) => {
      if (props.readOnly) {
        return;
      }

      // checkbox form.communicationdoc has been activated
      if (oldValues[4] === false && newValues[4] === true) {
        // check, if already 3 claims are communicated to the doc
        if (communicationDocumentCount.value === 3) {
          refConfirmCommunicationDoctor.value.open();
        } else {
          // immediately confirm
          confirmCommunicationdoc();
        }
      }
      // checkbox form.communicationpatient has been activated
      else if (oldValues[5] === false && newValues[5] === true) {
        showCommunicationPatientWarning();
      }
      // all other changes will be saved immediately
      else {
        save();
      }
    },
  );

  const showCommunicationPatientWarning = () => {
    // check if this is the first claim communicated to the patient
    // AND communicationPatientWarning layer is NOT disabled via configuration
    if (
      userHasSeenCommunicationPatient.value === false &&
      privacy.getConfiguration("communicationPatientWarning")?.amount !== false &&
      communicationPatientCount.value === 0 &&
      // skip the confirmation for patient communication on
      // all checks within "compliance" and "prevention"
      props.mcCheckId !== 18 && // noncompliance
      props.mcCheckId !== 27 && // explanationrequired
      props.mcCheckId !== 20 && // prevention
      props.mcCheckId !== 31 // interaction_food
    ) {
      userHasSeenCommunicationPatient.value = true;
      refConfirmCommunicationPatient.value.open();
    } else {
      // immediately confirm
      confirmCommunicationpatient();
    }
  };

  const triggerCloseClaim = inject("triggerCloseClaim");
  const closeClaimSiblings = inject("closeClaimSiblings");

  function toggleClaim() {
    if (open.value === false && typeof closeClaimSiblings === "function") {
      closeClaimSiblings(props.claim.id);
    }
    open.value = !open.value;
  }

  watch(triggerCloseClaim, (openClaimId) => {
    if (openClaimId !== props.claim.id) {
      open.value = false;
    }
  });

  function changeTab(newTab) {
    if (newTab === "tab-patient") {
      showCommunicationPatientWarning();
    }
    activeTab.value = newTab;
  }

  const handleResizeBreakpoint = (event) => {
    isFloating.value =
      event.detail.breakpoint === null ||
      event.detail.breakpoint === "xs" ||
      event.detail.breakpoint === "sm" ||
      event.detail.breakpoint === "md";
    if (!isFloating.value) {
      visibleTabPanels.value = true;
    }
  };

  onMounted(() => {
    globalThis.addEventListener("breakpoint", handleResizeBreakpoint);
  });

  onUnmounted(() => {
    globalThis.removeEventListener("breakpoint", handleResizeBreakpoint);
  });

  function changeInteractionTab(newTab) {
    if (isFloating.value && activeInteractionTab.value === newTab) {
      visibleTabPanels.value = !visibleTabPanels.value;
    } else {
      visibleTabPanels.value = true;
      activeInteractionTab.value = newTab;
    }
  }

  function openCommentsDialog() {
    refCommentsDialog.value.open();
  }

  function openConfidentialCommentsDialog() {
    refConfidentialCommentsDialog.value.open();
  }

  function openPatientTranslateDialog(force = false) {
    if (patientTranslateIsStatic.value) {
      // skip ai warning for static translations
      force = true;
    }

    let aiBasedPatientRephraseWarning = privacy.getConfiguration("aiBasedPatientRephraseWarning");

    if (force === true || aiBasedPatientRephraseWarning?.amount === false) {
      refPatientTranslateDialog.value.open(
        props.patientId,
        props.recordId,
        props.claim,
        props.mcCheckId,
        patientTranslateIsStatic.value,
      );
    } else {
      // show warning, when not explicitly hidden via users configuration
      refAiConfirmationDialog.value.open();
    }
  }

  function acceptPatientTranslation(result) {
    form.problempatient = result.problemtext;
    form.solutionpatient = result.solution;
    // always activate communication
    form.communicationpatient = true;
  }

  const communicationDocumentCount = computed(() => {
    return usePage().props.checks.data.reduce((total, check) => {
      return (
        total + check.claims.filter((claim) => claim.type === "redlight" && claim.communicationdoc === true).length
      );
    }, 0);
  });

  const communicationPatientCount = computed(() => {
    return usePage().props.checks.data.reduce((total, check) => {
      // skip all checks within "compliance" and "prevention" when
      // summing up the communicationPatientCount value
      if (
        check.mc_check_id === 18 || // noncompliance
        check.mc_check_id === 27 || // explanationrequired
        check.mc_check_id === 20 || // prevention
        check.mc_check_id === 31 // interaction_food
      ) {
        return total;
      }

      return (
        total + check.claims.filter((claim) => claim.type === "redlight" && claim.communicationpatient === true).length
      );
    }, 0);
  });

  const isRecordWithPcne = computed(() => {
    const version = pageProperties?.record?.metadata?.version?.core;
    // if we do not get a version, we assume, that the record has been
    // created recently, so without pcne documentation
    if (!version) {
      return false;
    }
    // otherwise we check, if the record has been created before 4.9
    // to allow viewing the existing pcne documentation

    // unfortunately we have a DIFFERENT core version schema
    // for each environment...
    if (pageProperties?.environmentConfig?.environment === "integration") {
      // testing: -01029d6d2-168
      const buildNumber = Number.parseInt(version.split("-").pop(), 10);
      if (!Number.isNaN(buildNumber) && buildNumber < 168) {
        return true;
      }
    } else if (pageProperties?.environmentConfig?.environment === "staging") {
      // stage: 4.8.2-03cb233a8-143
      // extract version before first "-" to ignore hash and build number
      const versionWithoutSuffix = version.split("-")[0];
      const [major, minor] = versionWithoutSuffix.split(".").map(Number);
      return major < 4 || (major === 4 && minor < 9);
    }

    // prod: 4.8.2, 4.9, ...
    const [major, minor] = version.split(".").map(Number);
    return major < 4 || (major === 4 && minor < 9);
  });

  function confirmCommunicationdoc() {
    // always disable "hidden"
    form.hidden = false;
    // set as active tab
    activeTab.value = "tab-doc";
    save();
  }

  function confirmCommunicationpatient() {
    // always disable "hidden"
    form.hidden = false;
    // set as active tab
    activeTab.value = "tab-patient";
    save();
  }
</script>

<template>
  <div
    :class="
      cn('border-l-4', {
        'border-very-high-risk': claim.class === 'red',
        'border-high-risk': claim.class === 'orange',
        'border-medium-risk': claim.class === 'yellow',
        'border-low-risk': claim.class === 'grey' || claim.class === 'gray',
        'border-white': claim.class === null,
      })
    "
    :data-test="getTestId('item')"
  >
    <a
      :id="claim.section"
      :data-risk-goto-target="claim.class + '-' + claim.check.mc_check_id"
      :class="
        cn(
          'group flex items-center space-x-2 bg-linear-to-t from-white p-2 text-gray-700! no-underline! hover:no-underline!',
          {
            'to-gray-100': !aiPrioritized,
            'to-fuchsia-200': aiPrioritized,
          },
        )
      "
      href="#"
      role="button"
      :aria-expanded="open"
      :aria-controls="claim.section + '-content'"
      :data-test="getTestId('item-toggle')"
      @click.prevent="toggleClaim()"
    >
      <MedIcon
        size="lg"
        icon="keyboard_arrow_down"
        :class="
          cn('transition-transform duration-200 print:hidden!', {
            'rotate-180': open,
          })
        "
      />

      <div
        :class="
          cn('line-clamp-2 flex-1 group-hover:text-mcred-500!', {
            'font-semibold': claim.communicationdoc || claim.communicationpatient,
            'line-through': claim.hidden,
          })
        "
      >
        <span>{{ claimSummary ?? "Check-Treffer " + claimNumber }}</span>

        <template v-if="hasAbdataDetails">
          <span v-if="interactionKeyMeasure.length > 0">{{ interactionKeyMeasure }}</span>

          - ABDATA:
          <template v-if="typeof claim.causedby.extra.rating !== 'undefined'">
            <!-- interaction, contraindication or questionable contraindication -->
            <span v-if="claim.causedby.extra.rating === 1" class="text-abdata-red">rot</span>
            <span v-if="claim.causedby.extra.rating === 2" class="text-abdata-orange">orange</span>
            <span v-if="claim.causedby.extra.rating === 3" class="text-abdata-yellow">gelb</span>
          </template>
          <template v-else-if="typeof claim.causedby.extra.relevance !== 'undefined'">
            <span v-if="claim.causedby.extra.relevance === 60" class="text-abdata-darkred">dunkelrot</span>
            <span v-if="claim.causedby.extra.relevance === 50" class="text-abdata-red">rot</span>
            <span v-if="claim.causedby.extra.relevance === 40" class="text-abdata-orange">orange</span>
            <span v-if="claim.causedby.extra.relevance === 30" class="text-abdata-yellow">gelb</span>
            <span v-if="claim.causedby.extra.relevance === 20" class="text-abdata-brown">braun</span>
            <span v-if="claim.causedby.extra.relevance === 10" class="text-abdata-blue">blau</span>
            <span v-if="claim.causedby.extra.relevance === 0" class="text-abdata-gray">grau</span>
          </template>
        </template>
      </div>

      <aside class="flex space-x-4 text-sm print:hidden">
        <template v-if="!readOnly">
          <div v-if="!open && claim.comment_count" class="flex items-center space-x-1">
            <ComponentIcon class="text-xl">mode_comment</ComponentIcon>
            <span>{{ claim.comment_count }}</span>
            <span v-if="claim.comment_count === 1">Kommentar</span>
            <span v-else>Kommentare</span>
          </div>

          <div v-if="!open && claim.confidential_comment_count" class="flex items-center space-x-1">
            <ComponentIcon custom="doctor" class="size-3" />
            <span>{{ claim.confidential_comment_count }}</span>
            <span v-if="claim.confidential_comment_count === 1">Arztrückmeldung</span>
            <span v-else>Arztrückmeldungen</span>
          </div>

          <div v-if="!claim.hidden && claim.communicationdoc" class="flex items-center space-x-1">
            <ComponentIcon class="text-xl">check</ComponentIcon>
            Arzt
          </div>

          <div v-if="!claim.hidden && claim.communicationpatient" class="flex items-center space-x-1">
            <ComponentIcon class="text-xl">check</ComponentIcon>
            Patient
          </div>

          <div v-if="claim.hidden">nicht relevant</div>
        </template>

        <MedTooltip v-if="aiPrioritized" content="Von der MediCheck-KI priorisiert">
          <ComponentIconAiSparkle class="h-5 w-auto" :data-test="getTestId('item-ai-prioritized-icon')" />
        </MedTooltip>
      </aside>
    </a>

    <div
      :id="claim.section + '-content'"
      :aria-labelledby="claim.section"
      :class="
        cn('relative items-start space-x-8 p-2 print:break-before-avoid-page', {
          'hidden print:flex!': !open,
          flex: open,
        })
      "
    >
      <main class="flex-1">
        <nav class="h-10 lg:h-auto print:hidden">
          <ul class="flex flex-wrap space-x-2 border-b border-gray-200">
            <li v-if="isTabVisible('tab-doc')">
              <button
                :class="
                  cn(
                    'flex items-center space-x-2 rounded-t-md p-2 font-semibold text-gray-900 hover:bg-gray-100 enabled:cursor-pointer',
                    {
                      'border-b-2 border-mcred bg-mcred-100/50 text-mcred hover:bg-mcred-100 hover:text-mcred-700':
                        activeTab === 'tab-doc',
                    },
                  )
                "
                :data-test="getTestId('item-tab-doc-button')"
                @click="changeTab('tab-doc')"
              >
                <MedTooltip v-if="!readOnly" content="Kommunikation im PDF">
                  <MedCheckbox
                    v-model="form.communicationdoc"
                    :variant="activeTab === 'tab-doc' ? 'primary' : 'default'"
                    :data-test="getTestId('item-tab-doc-communication-checkbox')"
                  />
                </MedTooltip>
                <span>an Arzt</span>
              </button>
            </li>

            <li v-if="isTabVisible('tab-patient')">
              <button
                :class="
                  cn(
                    'flex items-center space-x-2 rounded-t-md p-2 font-semibold text-gray-900 hover:bg-gray-100 enabled:cursor-pointer',
                    {
                      'border-b-2 border-mcred bg-mcred-100/50 text-mcred hover:bg-mcred-100 hover:text-mcred-700':
                        activeTab === 'tab-patient',
                    },
                  )
                "
                :data-test="getTestId('item-tab-patient-button')"
                @click="changeTab('tab-patient')"
              >
                <MedTooltip v-if="!readOnly" content="Kommunikation im PDF">
                  <MedCheckbox
                    v-model="form.communicationpatient"
                    :variant="activeTab === 'tab-patient' ? 'primary' : 'default'"
                    :data-test="getTestId('item-tab-patient-communication-checkbox')"
                  />
                </MedTooltip>
                <span>an Patienten</span>
              </button>
            </li>

            <li v-if="isTabVisible('tab-original')">
              <button
                :class="
                  cn('rounded-t-md p-2 font-semibold text-gray-900 hover:bg-gray-100 enabled:cursor-pointer', {
                    'border-b-2 border-mcred bg-mcred-100/50 text-mcred hover:bg-mcred-100 hover:text-mcred-700':
                      activeTab === 'tab-original',
                  })
                "
                :data-test="getTestId('item-tab-original-button')"
                @click="changeTab('tab-original')"
              >
                Originaltext
              </button>
            </li>
          </ul>
        </nav>

        <div class="print:space-y-2">
          <div
            v-if="isTabVisible('tab-doc')"
            :class="
              cn('print:hidden in-[.print-archive]:print:block in-[.print-doc]:print:block', {
                hidden: activeTab !== 'tab-doc',
              })
            "
            role="tabpanel"
          >
            <div class="hidden p-1 font-semibold print:break-after-avoid-page in-[.print-archive]:print:block">
              An Arzt:
            </div>

            <PagesRecordResultListClaimTextBox
              v-model:problem="form.problemdoc"
              v-model:solution="form.solutiondoc"
              :problem-original="claim.problem"
              :solution-original="claim.solution"
              :visible="open && activeTab === 'tab-doc'"
              :comment="claim.comment"
              :mc-check-id="mcCheckId"
              :claim-number="hasSiblingClaims ? claimNumber : null"
              :test-id="getTestId('item-tab-doc')"
              @open:core-info-dialog="(data) => refCoreInfoDialog.open(data)"
              @activate:communication="form.communicationdoc = true"
            />
          </div>

          <div
            v-if="isTabVisible('tab-patient')"
            :class="
              cn('print:hidden in-[.print-archive]:print:block in-[.print-patient]:print:block', {
                hidden: activeTab !== 'tab-patient',
              })
            "
            role="tabpanel"
          >
            <div class="hidden p-1 font-semibold print:break-after-avoid-page in-[.print-archive]:print:block">
              An Patienten:
            </div>
            <PagesRecordResultListClaimTextBox
              v-model:problem="form.problempatient"
              v-model:solution="form.solutionpatient"
              :problem-original="claim.problem"
              :solution-original="claim.solution"
              :visible="open && activeTab === 'tab-patient'"
              :comment="claim.comment"
              :mc-check-id="mcCheckId"
              :claim-number="hasSiblingClaims ? claimNumber : null"
              :test-id="getTestId('item-tab-patient')"
              @open:core-info-dialog="(data) => refCoreInfoDialog.open(data)"
              @activate:communication="form.communicationpatient = true"
            >
              <template v-if="!patientTranslateIsHidden" #ai-button>
                <DeveloperContainer v-if="claim.check.is_ai_translatable">
                  <MedButton
                    variant="dev"
                    as="a"
                    target="_blank"
                    class="print:hidden"
                    :href="$route('ai-debugger.index') + '?id=' + claim.id + '&feature=translate'"
                    :data-test="getTestId('ai-debug-translate-button')"
                  >
                    AI Debugger (translate)
                  </MedButton>
                </DeveloperContainer>

                <MedTooltip :disabled="patientTranslateButtonTooltip === null" :content="patientTranslateButtonTooltip">
                  <MedButton
                    :icon="patientTranslateIsStatic ? null : 'auto_awesome'"
                    class="print:hidden"
                    :disabled="patientTranslateButtonDisabled"
                    :data-test="getTestId('open-patient-translate-dialog-button')"
                    @click="openPatientTranslateDialog"
                  >
                    Sprache vereinfachen
                  </MedButton>
                </MedTooltip>
              </template>
            </PagesRecordResultListClaimTextBox>
          </div>

          <div
            v-if="isTabVisible('tab-original')"
            :class="
              cn('print:hidden', {
                hidden: activeTab !== 'tab-original',
              })
            "
            role="tabpanel"
          >
            <PagesRecordResultListClaimTextBox
              :visible="open && activeTab === 'tab-original'"
              :problem="claim.problem"
              :solution="claim.solution"
              :problem-original="claim.problem"
              :solution-original="claim.solution"
              :read-only="true"
              :comment="claim.comment"
              :mc-check-id="mcCheckId"
              :claim-number="hasSiblingClaims ? claimNumber : null"
              :test-id="getTestId('item-tab-original')"
              @open:core-info-dialog="(data) => refCoreInfoDialog.open(data)"
            />
          </div>
        </div>
      </main>

      <aside
        v-if="!readOnly"
        class="absolute right-0 w-1/4 min-w-64 bg-white pl-2 shadow-lg lg:relative lg:pl-0 lg:shadow-none print:relative print:min-w-auto print:shadow-none"
      >
        <nav class="print:hidden">
          <ul class="flex w-full flex-wrap justify-between space-x-2 border-b border-gray-200">
            <li>
              <button
                :class="
                  cn('rounded-t-md p-2 font-semibold text-gray-900 hover:bg-gray-100 enabled:cursor-pointer', {
                    'border-b-2 border-mcred bg-mcred-100/50 text-mcred hover:bg-mcred-100 hover:text-mcred-700 enabled:cursor-pointer':
                      activeInteractionTab === 'tab-pdf',
                  })
                "
                :data-test="getTestId('item-tab-pdf-button')"
                @click="changeInteractionTab('tab-pdf')"
              >
                Bericht
              </button>
            </li>

            <li v-if="isRecordWithPcne">
              <button
                :class="
                  cn('rounded-t-md p-2 font-semibold text-gray-900 hover:bg-gray-100 enabled:cursor-pointer', {
                    'border-b-2 border-mcred bg-mcred-100/50 text-mcred hover:bg-mcred-100 hover:text-mcred-700':
                      activeInteractionTab === 'tab-documentation',
                  })
                "
                :data-test="getTestId('item-tab-documentation-button')"
                @click="changeInteractionTab('tab-documentation')"
              >
                Doku
              </button>
            </li>

            <li class="flex-1" />

            <li>
              <MedBadge
                :variant="activeInteractionTab === 'tab-chat' ? 'primary' : 'default'"
                :label="claim?.comment_count"
                :data-test="getTestId('item-tab-chat-badge')"
              >
                <button
                  :class="
                    cn('rounded-t-md p-2 font-semibold text-gray-900 hover:bg-gray-100 enabled:cursor-pointer', {
                      'border-b-2 border-mcred bg-mcred-100/50 text-mcred hover:bg-mcred-100 hover:text-mcred-700':
                        activeInteractionTab === 'tab-chat',
                    })
                  "
                  :data-test="getTestId('item-tab-chat-button')"
                  @click="changeInteractionTab('tab-chat')"
                >
                  Team
                </button>
              </MedBadge>
            </li>
          </ul>
        </nav>

        <div
          :class="
            cn('mt-3', {
              block: visibleTabPanels,
              'hidden print:block': !visibleTabPanels,
            })
          "
        >
          <div :class="{'hidden print:block': activeInteractionTab !== 'tab-pdf'}" role="tabpanel">
            <PagesRecordResultListClaimInteractionPdf
              v-model:communicationdoc="form.communicationdoc"
              v-model:communicationpatient="form.communicationpatient"
              v-model:hidden="form.hidden"
              v-model:usernote="form.usernote"
              v-model:usernotepatient="form.usernotepatient"
              :claim="claim"
              :data-test="'listClaimItem-' + claim.section + '-interactionTab-pdf'"
              :test-id="getTestId('item-tab-pdf')"
              @open:confidential-comments-dialog="openConfidentialCommentsDialog()"
            />
          </div>

          <div
            v-if="isRecordWithPcne"
            :class="cn('print:hidden', {hidden: activeInteractionTab !== 'tab-documentation'})"
            role="tabpanel"
          >
            <PagesRecordResultListClaimInteractionDocumentation
              :mc-check-id="mcCheckId"
              :claim="claim"
              :test-id="getTestId('item-tab-documentation')"
              :is-record-with-pcne="isRecordWithPcne"
            />
          </div>

          <div :class="cn('print:hidden', {hidden: activeInteractionTab !== 'tab-chat'})" role="tabpanel">
            <PagesRecordResultListClaimInteractionChat
              :comments="claimComments"
              :comment-section="mcCheckId + '-' + claim.section"
              :patient-id="patientId"
              :record-id="recordId"
              @open:comments-dialog="openCommentsDialog()"
              @open:share-dialog="$emit('open:shareDialog')"
            />
          </div>
        </div>
      </aside>
    </div>

    <ComponentCoreInfoDialog ref="refCoreInfoDialog" />

    <ComponentCommentsDialog
      ref="refCommentsDialog"
      :record-id="recordId"
      :patient-id="patientId"
      :confidential="false"
      :comments="claimComments"
    />

    <ComponentCommentsDialog
      ref="refConfidentialCommentsDialog"
      :record-id="recordId"
      :patient-id="patientId"
      :confidential="true"
      :comments="claimConfidentialComments"
    />

    <ComponentConfirmationDialog
      ref="refAiConfirmationDialog"
      title="KI-gestützte Vereinfachung der Sprache starten?"
      primary-label="Verstanden"
      dialog-width="medium"
      @confirmed="openPatientTranslateDialog(true)"
    >
      <template #content>
        <p>
          Mithilfe dieser KI-basierten Funktion können Texte für den Patientenbericht in eine leicht verständliche
          Sprache umformuliert werden. Quelle für die Umformulierung ist der Originaltext des MediCheck. Der Schutz
          Ihrer Angaben und der darin enthaltenen Informationen ist jederzeit gewährleistet.
        </p>
        <p class="mt-4">
          Bitte beachten Sie, dass jede KI Fehler machen oder unvollständige Angaben erzeugen kann. Nutzen Sie daher den
          Originaltext des MediCheck als Ausgangspunkt und überprüfen Sie alle Fakten und wichtigen Informationen
          sorgfältig.
        </p>

        <ComponentDoNotShowAgain class="mt-4" settings-name="aiBasedPatientRephraseWarning" />
      </template>
    </ComponentConfirmationDialog>

    <ComponentPatientTranslateDialog
      ref="refPatientTranslateDialog"
      @accept="acceptPatientTranslation"
      @open:core-info-dialog="(data) => refCoreInfoDialog.open(data)"
    />

    <ComponentDialog
      ref="refConfirmCommunicationDoctor"
      dialog-title="Hinweis zur Kommunikation"
      :test-id="getTestId('item-note-on-communication-doc')"
      @primary-button-clicked="confirmCommunicationdoc()"
      @cancel-button-clicked="form.communicationdoc = false"
    >
      <template #content>
        <p>
          Sie haben bereits drei Checktreffer ausgewählt. Kollegen (v. a. Ärzte) bevorzugen kurze, kompakte
          Informationen. Wir empfehlen das Übermitteln weiterer Treffer auf AMTS-relevante Notwendigkeit zu prüfen.
        </p>
        <p class="mt-4">Nutzen Sie „Zusammenfassung der Analyse“ (s. u.).</p>
      </template>
    </ComponentDialog>

    <ComponentDialog
      ref="refConfirmCommunicationPatient"
      dialog-type="multiActions"
      dialog-title="Hinweis Ergebnisbericht Patient"
      :test-id="getTestId('item-note-on-communication-patient')"
      @primary-button-clicked="confirmCommunicationpatient()"
    >
      <template #content>
        <p>
          <strong>CAVE:</strong>
          Bitte achten Sie bei Auswahl des Checktreffers für den Ergebnisbericht auf eine patientengerechte
          Formulierung. Sie können hierfür unsere Funktion „Sprache vereinfachen“ benutzen oder die Texte selbst
          verbalisieren. Alle Texte können von Ihnen bearbeitet werden.
        </p>

        <ComponentDoNotShowAgain class="mt-4" settings-name="communicationPatientWarning" />
      </template>
    </ComponentDialog>
  </div>
</template>
